import { cloneDeep } from "lodash-es";
import type { FilterManagerFilter } from "./FilterManagerFilter";
import type { FilterManagerFilterOptions } from "~/modules/catalog/classes/FilterManager/FilterManagerFilterOptions";
import { FilterManagerFilterType } from "~/modules/catalog/classes/FilterManager/FilterManagerFilterType";
import type { FilterManagerFilterNumber } from "~/modules/catalog/classes/FilterManager/FilterManagerFilterNumber";
import type { FilterManagerState } from "~/modules/catalog/classes/FilterManager/FilterManagerState";

export class FilterManager {
  filterState: number = Date.now();
  filters: FilterManagerFilter[] = [];

  constructor(initialValues?: { filters?: FilterManagerFilter[] }) {
    this.filters = initialValues?.filters ?? [];
  }

  clear() {
    this.filters = [];
  }

  getCurrentState(): FilterManagerState {
    return {
      filters: cloneDeep(this.filters),
    };
  }

  setState(state: FilterManagerState): void {
    this.filters = state.filters;
    this.filterState = Date.now();
  }

  toggleOption(key: string, value: string) {
    const suboption = this.getOptionByKeyCreateIfNotExists(key, value);
    suboption.selected = !suboption.selected;
  }

  selectOption(key: string, value: string) {
    const suboption = this.getOptionByKeyCreateIfNotExists(key, value);
    suboption.selected = true;
  }

  deselectOption(key: string, value: string) {
    const suboption = this.getOptionByKeyCreateIfNotExists(key, value);
    suboption.selected = false;
  }

  clearOptionSelections(key?: string) {
    if (key) {
      const option = this.filters.find((o) => o.key === key);
      if (option && FilterManager.isFilterOptions(option)) {
        option.options.forEach((o) => (o.selected = false));
      }
      return;
    }

    this.filters.forEach((option) => {
      if (option && FilterManager.isFilterOptions(option)) {
        option.options.forEach((o2) => (o2.selected = false));
      }
    });
  }

  updateNumber(key: string, min: number | string, max: number | string) {
    const slideIndex = this.filters.findIndex((s) => s.key === key);
    let slider: FilterManagerFilterNumber;

    if (slideIndex === -1) {
      slider = {
        key,
        end: max.toString(),
        start: min.toString(),
        min: min.toString(),
        max: max.toString(),
        label: key,
        type: FilterManagerFilterType.number,
      };
      this.filters.push(slider);
    } else if (FilterManager.isFilterNumber(this.filters[slideIndex])) {
      slider = this.filters[slideIndex] as FilterManagerFilterNumber;
      slider.start = min.toString();
      slider.end = max.toString();
    } else {
      throw new Error(`Filter ${key} is not a slider filter.`);
    }
  }

  currentSelection(key: string): string[] | undefined {
    const opt = this.filters.find((o) => o.key === key);
    if (opt && FilterManager.isFilterOptions(opt)) {
      const selectedOptions = opt.options.filter((subOpt) => subOpt.selected);
      return selectedOptions.map((s) => s.value);
    }

    if (opt && FilterManager.isFilterNumber(opt) && opt.start && opt.end) {
      return [opt.start, opt.end];
    }

    return undefined;
  }

  toURLParams(searchParams?: URLSearchParams): string {
    const params = searchParams || new URLSearchParams();
    for (const filter of this.filters) {
      if (FilterManager.isFilterOptions(filter)) {
        const selected = filter.options.filter(
          (subOption) => subOption.selected,
        );
        if (selected.length === 0) {
          params.delete(`o_${filter.key}`);
          continue;
        }
        params.set(`o_${filter.key}`, selected.map((s) => s.value).join(","));
      } else if (FilterManager.isFilterNumber(filter)) {
        if (!filter.start || !filter.end) {
          params.delete(`s_${filter.key}`);
          continue;
        }
        params.set(`s_${filter.key}`, `${filter.start}-${filter.end}`);
      }
    }

    for (const [key] of params.entries()) {
      if (key.startsWith("o_") || key.startsWith("s_")) {
        const filterKey = key.substring(2);
        const filter = this.filters.find((f) => f.key === filterKey);
        if (!filter) {
          params.delete(key);
        }
      }
    }

    return params.toString();
  }

  readFromURLParams(params: URLSearchParams) {
    this.clearOptionSelections(undefined);
    for (const [key, value] of params.entries()) {
      if (key.startsWith("o_")) {
        const optionKey = key.substring(2);
        value.split(",").forEach((v) => this.selectOption(optionKey, v));
      } else if (key.startsWith("s_")) {
        const sliderKey = key.substring(2);
        this.updateNumber(
          sliderKey,
          parseFloat(value.split("-")[0]),
          parseFloat(value.split("-")[1]),
        );
      }
    }
  }

  selectedFilters() {
    const selectedFilters: Array<{
      key: string;
      label: string;
      type: FilterManagerFilterType;
      value: string;
      valueLabel: string;
    }> = [];
    for (const filter of this.filters) {
      if (FilterManager.isFilterOptions(filter)) {
        const selected = filter.options.filter(
          (subOption) => subOption.selected,
        );
        if (selected.length === 0) {
          continue;
        }
        for (const option of selected) {
          selectedFilters.push({
            key: filter.key,
            label: filter.label,
            type: filter.type,
            value: option.value,
            valueLabel: option.name,
          });
        }
      } else if (FilterManager.isFilterNumber(filter)) {
        if (!filter.start || !filter.end) {
          continue;
        }
        selectedFilters.push({
          key: filter.key,
          label: filter.label,
          type: filter.type,
          value: `${filter.start}-${filter.end}`,
          valueLabel: `${filter.start}-${filter.end}`,
        });
      }
    }

    return selectedFilters;
  }

  private getOptionByKeyCreateIfNotExists(
    key: string,
    value: string,
  ): FilterManagerFilterOptions["options"][number] {
    const optionIndex = this.filters.findIndex((o) => o.key === key);
    let option: FilterManagerFilterOptions;

    if (optionIndex === -1) {
      option = {
        key,
        label: key,
        options: [],
        type: FilterManagerFilterType.options,
      };
      this.filters.push(option);
    } else if (FilterManager.isFilterOptions(this.filters[optionIndex])) {
      option = this.filters[optionIndex];
    } else {
      throw new Error(`Filter ${key} is not an option filter.`);
    }

    const subOptionIndex = option.options.findIndex((o) => o.value === value);

    if (subOptionIndex === -1) {
      option.options.push({
        count: 1,
        name: value,
        selected: false,
        value,
      });

      return option.options[option.options.length - 1];
    }

    return option.options[subOptionIndex];
  }

  reset(): void {
    this.filters.forEach((el) => {
      this.resetFilter(el);
    });
  }

  resetFilter(filter: {
    key: string;
    type: FilterManagerFilterType;
    value?: string;
  }): void {
    switch (true) {
      case filter.type === FilterManagerFilterType.number:
        this.updateNumber(filter.key, "" as any, "" as any);
        break;
      case filter.type === FilterManagerFilterType.options:
        if (filter.value) {
          this.deselectOption(filter.key, filter.value);
        } else {
          this.clearOptionSelections(filter.key);
        }
        break;
    }
  }

  hasSelected(key: string): boolean {
    return this.selectedFilters().some((s) => s.key === key);
  }

  static isFilterNumber(
    filter: FilterManagerFilter,
  ): filter is FilterManagerFilterNumber {
    return filter.type === FilterManagerFilterType.number;
  }

  static isFilterOptions(
    filter: FilterManagerFilter,
  ): filter is FilterManagerFilterOptions {
    return filter.type === FilterManagerFilterType.options;
  }
}
